import React, { useState, useMemo } from 'react';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';
import ShiftDetailModal from './ShiftDetailModal';

const ShiftPlanCalendar = ({
  plan,
  selectedShifts = [],
  onShiftSelect,
  onShiftRemove,
  readOnly = false,
  allowViewDetails = false, // Allow clicking to view details even when readOnly
  teamMembersWithProfile = [], // Array of { uid, name, photoURL, displayName }
  otherMembersShifts = [], // Shifts submitted by other members (for display only)
  pendingSwapRequests = [], // Pending swap requests for this plan
  onSwapRequest = null // Callback when user wants to request swap
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showShiftModal, setShowShiftModal] = useState(false);
  
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (plan?.targetMonth) {
      const [year, month] = plan.targetMonth.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, 1);
    }
    return new Date();
  });

  // Calculate date range from plan
  const dateRange = useMemo(() => {
    if (!plan?.targetMonth || !plan?.monthsAhead) return [];
    
    const startDate = new Date(`${plan.targetMonth}-01`);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + plan.monthsAhead);
    
    const dates = [];
    const current = new Date(startDate);
    while (current < endDate) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [plan]);

  // Group dates by month
  const datesByMonth = useMemo(() => {
    const grouped = {};
    dateRange.forEach(date => {
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(date);
    });
    return grouped;
  }, [dateRange]);

  // Get shifts for a specific date (user's own shifts)
  const getShiftsForDate = (date) => {
    const dateKey = date.toISOString().split('T')[0];
    return selectedShifts.filter(shift => shift.date === dateKey);
  };

  // Get other members' shifts for a specific date
  const getOtherMembersShiftsForDate = (date) => {
    const dateKey = date.toISOString().split('T')[0];
    return otherMembersShifts.filter(shift => shift.date === dateKey);
  };

  // Get pending swap requests count for a specific date
  const getPendingSwapRequestsForDate = (date) => {
    const dateKey = date.toISOString().split('T')[0];
    return pendingSwapRequests.filter(swap => {
      // For plan-based swaps, originalShift.date is a string (YYYY-MM-DD)
      if (swap.originalShift?.date) {
        return swap.originalShift.date === dateKey;
      }
      // For shift-based swaps, check startTime
      if (swap.originalShift?.startTime) {
        const swapDate = swap.originalShift.startTime.toDate 
          ? swap.originalShift.startTime.toDate().toISOString().split('T')[0]
          : swap.originalShift.startTime.seconds 
          ? new Date(swap.originalShift.startTime.seconds * 1000).toISOString().split('T')[0]
          : null;
        return swapDate === dateKey;
      }
      return false;
    });
  };

  // Get user profile image
  const getUserProfileImage = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.photoURL || null;
  };

  // Get user display name
  const getUserDisplayName = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.displayName || member?.name || 'Unknown';
  };

  // Get shift type color
  const getShiftTypeColor = (shiftType) => {
    switch (shiftType) {
      case SHIFT_TYPES.MORNING:
        return 'bg-green-500';
      case SHIFT_TYPES.AFTERNOON:
        return 'bg-yellow-500';
      case SHIFT_TYPES.NIGHT:
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Get shift type icon
  const getShiftTypeIcon = (shiftType) => {
    switch (shiftType) {
      case SHIFT_TYPES.MORNING:
        return '☀️';
      case SHIFT_TYPES.AFTERNOON:
        return '🌤️';
      case SHIFT_TYPES.NIGHT:
        return '🌙';
      default:
        return '';
    }
  };

  // Get shift type label
  const getShiftTypeLabel = (shiftType) => {
    switch (shiftType) {
      case SHIFT_TYPES.MORNING:
        return 'เช้า';
      case SHIFT_TYPES.AFTERNOON:
        return 'บ่าย';
      case SHIFT_TYPES.NIGHT:
        return 'ดึก';
      default:
        return shiftType;
    }
  };

  // Handle date click
  const handleDateClick = (date) => {
    if (readOnly && !allowViewDetails) return;
    
    setSelectedDate(date);
    // If readOnly and allowViewDetails, show detail modal instead
    if (readOnly && allowViewDetails) {
      setShowShiftModal(true);
    } else {
      setShowShiftModal(true);
    }
  };

  // Handle shift type selection from modal
  const handleShiftTypeSelect = (shiftType) => {
    if (!selectedDate) return;
    
    const dateKey = selectedDate.toISOString().split('T')[0];
    const existingShifts = getShiftsForDate(selectedDate);
    
    // Check if this shift type already exists
    const existingShift = existingShifts.find(s => s.shiftType === shiftType);
    
    if (existingShift) {
      // Remove if already exists
      onShiftRemove(existingShift);
    } else {
      // Add new shift
      onShiftSelect({
        date: dateKey,
        shiftType: shiftType,
        notes: ''
      });
    }
    
    setShowShiftModal(false);
    setSelectedDate(null);
  };

  // Handle remove all shifts for date
  const handleRemoveAllShifts = () => {
    if (!selectedDate) return;
    
    const existingShifts = getShiftsForDate(selectedDate);
    existingShifts.forEach(shift => onShiftRemove(shift));
    
    setShowShiftModal(false);
    setSelectedDate(null);
  };

  // Render calendar for a specific month
  const renderMonthCalendar = (monthKey, dates) => {
    const [year, month] = monthKey.split('-');
    const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    
    // Get first day of month and number of days
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Thai month names
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน',
      'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม',
      'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    
    const monthName = thaiMonths[monthDate.getMonth()];
    const yearBE = monthDate.getFullYear() + 543;
    
    // Create calendar grid
    const calendarDays = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      calendarDays.push(date);
    }
    
    return (
      <div key={monthKey} className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {monthName} {yearBE}
        </h3>
        
        {/* Calendar Grid */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map((day, idx) => (
              <div
                key={idx}
                className="text-center text-sm font-medium text-gray-600 py-2"
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, idx) => {
              if (!date) {
                return <div key={idx} className="aspect-square" />;
              }
              
              const dateKey = date.toISOString().split('T')[0];
              const shifts = getShiftsForDate(date);
              const otherShifts = getOtherMembersShiftsForDate(date);
              const pendingSwaps = getPendingSwapRequestsForDate(date);
              const isInRange = dates.some(d => d.toISOString().split('T')[0] === dateKey);
              const isToday = dateKey === new Date().toISOString().split('T')[0];
              const totalShifts = shifts.length + otherShifts.length;
              const swapRequestCount = pendingSwaps.length;
              
              return (
                <div
                  key={idx}
                  onClick={() => isInRange && (!readOnly || allowViewDetails) && handleDateClick(date)}
                  className={`
                    aspect-square border rounded-lg p-1 transition-all
                    ${!isInRange ? 'opacity-30 cursor-not-allowed' : ''}
                    ${isToday ? 'ring-2 ring-blue-500' : ''}
                    ${readOnly && !allowViewDetails ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'}
                    ${totalShifts > 0 ? 'border-2 border-blue-300' : 'border-gray-200'}
                  `}
                >
                  {/* Date number with swap request indicator */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-medium text-gray-700">
                      {date.getDate()}
                    </div>
                    {swapRequestCount > 0 && (
                      <div className="w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center" title={`${swapRequestCount} คำขอแลกเวร`}>
                        {swapRequestCount}
                      </div>
                    )}
                  </div>
                  
                  {/* User's own shifts (highlighted) */}
                  {shifts.length > 0 && (
                    <div className="flex flex-col gap-0.5 mb-1">
                      {shifts.map((shift, shiftIdx) => {
                        const userPhoto = shift.userId ? getUserProfileImage(shift.userId) : null;
                        const userName = shift.userId ? getUserDisplayName(shift.userId) : '';
                        
                        return (
                          <div
                            key={`own-${shiftIdx}`}
                            className={`
                              ${getShiftTypeColor(shift.shiftType)}
                              text-white text-[10px] px-1 py-0.5 rounded
                              flex items-center gap-1 ring-2 ring-white
                            `}
                            title={`${getShiftTypeLabel(shift.shiftType)} - ${userName} (ของคุณ)`}
                          >
                            {/* Member Avatar */}
                            {userPhoto ? (
                              <img
                                src={userPhoto}
                                alt={userName}
                                className="w-4 h-4 rounded-full object-cover border border-white"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-4 h-4 rounded-full bg-white/30 flex items-center justify-center text-white text-[8px] font-semibold ${
                                userPhoto ? 'hidden' : 'flex'
                              }`}
                            >
                              {userName?.charAt(0) || '?'}
                            </div>
                            
                            {/* Shift Icon */}
                            <span className="text-xs">{getShiftTypeIcon(shift.shiftType)}</span>
                            
                            {/* Shift Label (hidden on small screens) */}
                            <span className="hidden sm:inline text-[9px] truncate flex-1">
                              {getShiftTypeLabel(shift.shiftType)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Other members' shifts (lighter, for reference) */}
                  {otherShifts.length > 0 && (
                    <div className="flex flex-wrap gap-0.5">
                      {otherShifts.slice(0, 3).map((shift, shiftIdx) => {
                        const userPhoto = shift.userId ? getUserProfileImage(shift.userId) : null;
                        const userName = shift.userName || (shift.userId ? getUserDisplayName(shift.userId) : '');
                        
                        return (
                          <div
                            key={`other-${shiftIdx}`}
                            className={`
                              ${getShiftTypeColor(shift.shiftType)}
                              opacity-60 text-white text-[8px] px-0.5 py-0.5 rounded
                              flex items-center gap-0.5
                            `}
                            title={`${getShiftTypeLabel(shift.shiftType)} - ${userName}`}
                          >
                            {/* Small Avatar */}
                            {userPhoto ? (
                              <img
                                src={userPhoto}
                                alt={userName}
                                className="w-3 h-3 rounded-full object-cover border border-white"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-3 h-3 rounded-full bg-white/30 flex items-center justify-center text-white text-[6px] font-semibold ${
                                userPhoto ? 'hidden' : 'flex'
                              }`}
                            >
                              {userName?.charAt(0) || '?'}
                            </div>
                            <span className="text-[8px]">{getShiftTypeIcon(shift.shiftType)}</span>
                          </div>
                        );
                      })}
                      {otherShifts.length > 3 && (
                        <div className="text-[8px] text-gray-500 px-0.5">
                          +{otherShifts.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Multiple shifts indicator */}
                  {totalShifts > 1 && (
                    <div className="text-[8px] text-gray-500 mt-0.5 text-center">
                      {shifts.length > 0 && otherShifts.length > 0 && (
                        <span className="text-blue-600 font-semibold">
                          {shifts.length} ของคุณ
                        </span>
                      )}
                      {otherShifts.length > 0 && (
                        <span className="text-gray-500">
                          {otherShifts.length > 0 && shifts.length > 0 && ' • '}
                          {otherShifts.length} คนอื่น
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-gray-700">เวรเช้า</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-gray-700">เวรบ่าย</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-gray-700">เวรดึก</span>
          </div>
          {!readOnly && (
            <div className="text-gray-500 text-xs">
              💡 คลิกวันที่เพื่อเลือก/เปลี่ยนเวร
            </div>
          )}
          {readOnly && allowViewDetails && (
            <div className="text-gray-500 text-xs">
              💡 คลิกวันที่เพื่อดูรายละเอียดเวร
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {Object.entries(datesByMonth).map(([monthKey, dates]) =>
        renderMonthCalendar(monthKey, dates)
      )}
      
      {/* Shift Detail Modal (for approved plans) */}
      {readOnly && allowViewDetails && showShiftModal && selectedDate && plan && (
        <ShiftDetailModal
          isOpen={showShiftModal}
          onClose={() => {
            setShowShiftModal(false);
            setSelectedDate(null);
          }}
          date={selectedDate}
          plan={plan}
          teamMembersWithProfile={teamMembersWithProfile}
        />
      )}

      {/* Shift Selection Modal (for editing) */}
      {!readOnly && showShiftModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              เลือกประเภทเวร
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              วันที่ {selectedDate.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            
            {/* Current shifts for this date (user's own) */}
            {getShiftsForDate(selectedDate).length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">เวรที่เลือกแล้ว:</p>
                <div className="flex flex-wrap gap-2">
                  {getShiftsForDate(selectedDate).map((shift, idx) => {
                    const userPhoto = shift.userId ? getUserProfileImage(shift.userId) : null;
                    const userName = shift.userId ? getUserDisplayName(shift.userId) : '';
                    
                    return (
                      <div
                        key={idx}
                        className={`${getShiftTypeColor(shift.shiftType)} text-white text-xs px-2 py-1 rounded flex items-center gap-1`}
                      >
                        {userPhoto ? (
                          <img
                            src={userPhoto}
                            alt={userName}
                            className="w-4 h-4 rounded-full object-cover border border-white"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-4 h-4 rounded-full bg-white/30 flex items-center justify-center text-white text-[8px] font-semibold ${
                            userPhoto ? 'hidden' : 'flex'
                          }`}
                        >
                          {userName?.charAt(0) || '?'}
                        </div>
                        <span>{getShiftTypeIcon(shift.shiftType)}</span>
                        <span>{getShiftTypeLabel(shift.shiftType)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Other members' shifts for this date */}
            {getOtherMembersShiftsForDate(selectedDate).length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-900 mb-2">เพื่อนที่ลงเวรวันเดียวกัน:</p>
                <div className="space-y-2">
                  {getOtherMembersShiftsForDate(selectedDate).map((shift, idx) => {
                    const userPhoto = shift.userId ? getUserProfileImage(shift.userId) : null;
                    const userName = shift.userName || (shift.userId ? getUserDisplayName(shift.userId) : 'Unknown');
                    
                    return (
                      <div
                        key={idx}
                        className={`${getShiftTypeColor(shift.shiftType)} text-white text-sm px-3 py-2 rounded-lg flex items-center gap-2 opacity-90`}
                      >
                        {/* Member Avatar */}
                        {userPhoto ? (
                          <img
                            src={userPhoto}
                            alt={userName}
                            className="w-6 h-6 rounded-full object-cover border-2 border-white"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-6 h-6 rounded-full bg-white/30 flex items-center justify-center text-white text-xs font-semibold ${
                            userPhoto ? 'hidden' : 'flex'
                          }`}
                        >
                          {userName?.charAt(0) || '?'}
                        </div>
                        
                        {/* Member Name and Shift Info */}
                        <div className="flex-1">
                          <p className="font-medium text-white">{userName}</p>
                          <p className="text-xs text-white/80">
                            {getShiftTypeIcon(shift.shiftType)} {getShiftTypeLabel(shift.shiftType)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Shift type buttons - Only show if not read-only */}
            {!readOnly && (
              <div className="space-y-2 mb-4">
                {Object.values(SHIFT_TYPES).map(shiftType => {
                  const existingShift = getShiftsForDate(selectedDate).find(s => s.shiftType === shiftType);
                  return (
                    <button
                      key={shiftType}
                      onClick={() => handleShiftTypeSelect(shiftType)}
                      className={`
                        w-full p-3 rounded-lg border-2 transition-all
                        ${existingShift
                          ? `${getShiftTypeColor(shiftType)} text-white border-transparent`
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                        }
                      `}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-xl">{getShiftTypeIcon(shiftType)}</span>
                        <span className="font-medium">{getShiftTypeLabel(shiftType)}</span>
                        {existingShift && <span className="text-sm">(คลิกเพื่อลบ)</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Swap Request Button */}
            {!readOnly && onSwapRequest && (
              <button
                onClick={() => {
                  const userShifts = getShiftsForDate(selectedDate);
                  const otherShifts = getOtherMembersShiftsForDate(selectedDate);
                  if (userShifts.length > 0 || otherShifts.length > 0) {
                    onSwapRequest(selectedDate, userShifts, otherShifts);
                    setShowShiftModal(false);
                    setSelectedDate(null);
                  }
                }}
                className="w-full mb-2 p-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center justify-center gap-2 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                ขอแลกเวร
              </button>
            )}
            
            {/* Remove all button - Only show if not read-only */}
            {!readOnly && getShiftsForDate(selectedDate).length > 0 && (
              <button
                onClick={handleRemoveAllShifts}
                className="w-full p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition"
              >
                ลบเวรทั้งหมดในวันนี้
              </button>
            )}
            
            {/* Close button */}
            <button
              onClick={() => {
                setShowShiftModal(false);
                setSelectedDate(null);
              }}
              className="w-full mt-2 p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              ปิด
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftPlanCalendar;

